/**
 * Flows list — all WhatsApp Flows authored in CCMS. Create from scratch or from a
 * template; click a flow to manage / publish / send it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconRocket, IconPlus, IconRefresh, IconInfoCircle, IconTemplate, IconChevronDown } from '@tabler/icons-react';
import { Stack, Group, Text, Badge, Button, Loader, Alert, Menu } from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { ChannelPage } from '../../_shared/ChannelPage';
import { useWhatsAppFlows, type FlowListItem } from '../../_shared/useWhatsAppFlows';
import { FLOW_TEMPLATES } from '../../_shared/flowDefinition';

const BASE = '/admin/communications/whatsapp/flows';
export const FLOW_STATUS: Record<number, { label: string; color: string }> = {
  0: { label: 'Draft', color: 'gray' },
  1: { label: 'Published', color: 'green' },
  2: { label: 'Archived', color: 'orange' },
};

export function fmtFlowDate(ymd?: number | null): string {
  if (!ymd) return '—';
  const s = String(Math.trunc(ymd)).padStart(14, '0');
  return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)} ${s.slice(8, 10)}:${s.slice(10, 12)}`;
}

export function FlowsListPage() {
  const navigate = useNavigate();
  const { listFlows } = useWhatsAppFlows();
  const [loading, setLoading] = useState(false);
  const [flows, setFlows] = useState<FlowListItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setFlows(await listFlows()); }
    catch (err: any) { setError(err?.message || 'Failed to load flows'); }
    finally { setLoading(false); }
  }, [listFlows]);

  useEffect(() => { load(); }, [load]);

  const createMenu = (
    <Menu position="bottom-end" withinPortal>
      <Menu.Target>
        <Button leftSection={<IconPlus size={16} />} rightSection={<IconChevronDown size={14} />} gradientVariant="primary">
          Create flow
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconRocket size={14} />} onClick={() => navigate(`${BASE}/new`)}>Blank flow</Menu.Item>
        <Menu.Label>Start from a template</Menu.Label>
        {FLOW_TEMPLATES.map((t) => (
          <Menu.Item key={t.key} leftSection={<IconTemplate size={14} />} onClick={() => navigate(`${BASE}/new?template=${t.key}`)}>
            {t.name}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );

  return (
    <ChannelPage
      icon={<IconRocket size={28} />}
      title="Flows"
      subtitle="Interactive forms & menus you can send inside WhatsApp."
      actions={createMenu}
    >
      <Stack gap="md">
        <Alert color="blue" icon={<IconInfoCircle size={16} />} title="What is a WhatsApp Flow?">
          A Flow is an interactive form or menu (sign-up, booking, survey…) that opens right inside the chat — customers
          fill it in without leaving WhatsApp. Build one visually here, publish it once in the 360dialog/Meta hub, then
          send it to your contacts. No coding or JSON needed.
        </Alert>

        <Group justify="space-between">
          <Text size="sm" c="dimmed">{flows.length} flow(s)</Text>
          <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={load} loading={loading}>Refresh</Button>
        </Group>

        {error && <Alert color="red">{error}</Alert>}

        {loading && flows.length === 0 ? (
          <Group justify="center" p="xl"><Loader size="lg" /></Group>
        ) : flows.length === 0 ? (
          <Card withBorder padding="xl">
            <Stack gap="sm" align="center">
              <IconRocket size={36} opacity={0.5} />
              <Text fw={500}>Create your first flow</Text>
              <Text size="sm" c="dimmed" ta="center">Start from scratch or pick a ready-made template — you can edit everything.</Text>
              <Group gap="xs" mt="xs">
                <Button variant="light" onClick={() => navigate(`${BASE}/new`)}>Blank flow</Button>
                {FLOW_TEMPLATES.map((t) => (
                  <Button key={t.key} variant="default" size="xs" onClick={() => navigate(`${BASE}/new?template=${t.key}`)}>{t.name}</Button>
                ))}
              </Group>
            </Stack>
          </Card>
        ) : (
          <Stack gap="xs">
            {flows.map((f) => (
              <Card key={f.flowId} withBorder padding="sm" style={{ cursor: 'pointer' }} onClick={() => navigate(`${BASE}/${f.flowId}`)}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={2} style={{ minWidth: 0 }}>
                    <Group gap="sm" wrap="nowrap">
                      <Text size="sm" fw={500} truncate>{f.name}</Text>
                      <Badge size="xs" variant="light" color={FLOW_STATUS[f.statusId]?.color}>{FLOW_STATUS[f.statusId]?.label ?? f.statusId}</Badge>
                      {f.category && <Badge size="xs" variant="light" color="grape">{f.category}</Badge>}
                    </Group>
                    <Text size="9px" c="dimmed">
                      {f.screenCount} screen{f.screenCount === 1 ? '' : 's'} · updated {fmtFlowDate(f.modifiedDateTime)}
                      {f.metaFlowId ? ` · Flow ID ${f.metaFlowId}` : ''}
                    </Text>
                  </Stack>
                </Group>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </ChannelPage>
  );
}
